import * as Yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum ABATestItemsKeys {
  itemNumber = 'itemNumber',
  description = 'description',
  manufacturer = 'manufacturer',
  model = 'model',
  serialNumber = 'serialNumber',
  licenseNumber = 'licenseNumber',
  yearOfManufacture = 'yearOfManufacture',
  radius = 'radius',
  test = 'test',
  safeWorkingLoad = 'safeWorkingLoad',
}

const ABATestItemsSchema = Yup.object().shape({
  [ABATestItemsKeys.itemNumber]: Yup.number().nullable().optional(),
  [ABATestItemsKeys.description]: Yup.string().nullable().optional(),
  [ABATestItemsKeys.manufacturer]: Yup.string().nullable().optional(),
  [ABATestItemsKeys.model]: Yup.string().nullable().optional(),
  [ABATestItemsKeys.serialNumber]: Yup.string().nullable().optional(),
  [ABATestItemsKeys.licenseNumber]: Yup.string().nullable().optional(),
  [ABATestItemsKeys.yearOfManufacture]: Yup.string().nullable().optional(),
  [ABATestItemsKeys.radius]: Yup.string().nullable().optional(),
  [ABATestItemsKeys.test]: Yup.string().nullable().optional(),
  [ABATestItemsKeys.safeWorkingLoad]: Yup.string().nullable().optional(),
})

export enum ABADefectItemsKeys {
  itemNumber = 'itemNumber',
  subject = 'subject',
  comments = 'comments',
}

const ABADefectItemsSchema = Yup.object().shape({
  [ABADefectItemsKeys.itemNumber]: Yup.number().nullable().optional(),
  [ABADefectItemsKeys.subject]: Yup.string().nullable().optional(),
  [ABADefectItemsKeys.comments]: Yup.string().nullable().optional(),
})

export const abaReportInspectionFileDataSchema = Yup.object().shape({
  currentReviewNumber: Yup.string().nullable().optional(),
  currentTestDate: Yup.string().nullable().optional(),
  nextTestDate: Yup.string().nullable().optional(),
  reviewPublicationDate: Yup.string().nullable().optional(),
  customer: Yup.object().shape({
    name: Yup.string().nullable().optional(),
    address: Yup.string().nullable().optional(),
    contactName: Yup.string().nullable().optional(),
    contactRole: Yup.string().nullable().optional(),
    contactPhone: Yup.string().nullable().optional(),
    contactEmail: Yup.string().nullable().optional(),
  }),
  note: Yup.string().nullable().optional(),
  facilityDescription: Yup.string().nullable().optional(),
  facility: Yup.object().shape({
    name: Yup.string().nullable().optional(),
    address: Yup.string().nullable().optional(),
    mobile: Yup.string().nullable().optional(),
    contactName: Yup.string().nullable().optional(),
  }),
  tests: Yup.object().shape({
    afterStructuralChange: Yup.string().nullable().optional(),
    afterRepair: Yup.string().nullable().optional(),
    periodic: Yup.string().nullable().optional(),
    initial: Yup.string().nullable().optional(),
  }),
  testItems: Yup.array().of(ABATestItemsSchema).required(),
  defectItems: Yup.array().of(ABADefectItemsSchema).required(),
  safetyNotes: Yup.array()
    .of(
      Yup.object().shape({
        title: Yup.string().nullable().optional(),
      }),
    )
    .required(),
  signature: Yup.string().nullable().optional(),
})

export type AbaReportInspectionFileData = Yup.InferType<typeof abaReportInspectionFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<AbaReportInspectionFileData>

export const abaReportInspectionFileMappings: RequiredFileData = {
  currentReviewNumber: '1a4bee40-142c-418d-a05b-92601ac6ceb4',
  currentTestDate: '52b9b06f-15b9-46a9-bc4b-08e9755868b8',
  nextTestDate: '9d8c736e-9f20-461e-a5fe-556928f0a524',
  reviewPublicationDate: '8b1fc50c-f1b4-49fb-8aa0-a92fa0d1f3b2',
  customer: {
    name: '425ad02d-58d8-46ab-86ce-af6d0d65db94',
    address: 'db781bb3-10aa-4f37-ad96-943a445dd6bc',
    contactName: '9a2b8423-ec71-45fd-a4dd-1ec3d2e4b2c3',
    contactRole: '124a9af5-6ea9-46d0-909a-51c18678c203',
    contactPhone: '95afaa8f-976a-479a-b685-825e3375a8fa',
    contactEmail: 'eff4eae0-4c88-47aa-a391-ff69360a23ef',
  },
  note: '47ccfb65-20d4-4418-8e81-885680375a70',
  facilityDescription: '6121ca67-d2f8-45ad-8bcb-050e6829ea2a',
  facility: {
    name: '50bb3402-5906-42d0-bd3b-67c066a1b6c3',
    address: '9a367140-8f1c-4bd3-b5cc-39b2fe421adb',
    mobile: '09ecd1fd-d6fc-4b9f-b014-856904a7bfb3',
    contactName: '4b0b396e-6f5a-4393-81f9-52462b0b15df',
  },
  tests: {
    afterStructuralChange: 'cb201685-3582-4649-a5e6-ad6c9e620515',
    afterRepair: '8cd15ae5-31b3-4613-91b0-073f4b6125d6',
    periodic: 'b6c422fb-e93c-458f-be60-10db151d0cb0',
    initial: '8fd460ff-8e94-4542-80ee-28862c18cf67',
  },
  testItems: [
    {
      itemNumber: null,
      description: '38681667-9e32-401d-976d-29ca36a73ed6',
      manufacturer: '1debe5eb-04f1-4839-ae59-0c2cf61d9d88',
      model: '909ed769-013c-42a9-a05c-5e54c651aa2c',
      serialNumber: 'b486be6d-0271-49f9-b9a8-b2c03f860787',
      licenseNumber: '3b7cde5a-668a-46ea-b321-6f0bf4ff29fa',
      yearOfManufacture: '4e1a2f05-a078-4b21-a2cc-e76bfcb51393',
      radius: '1eb0ef2f-caf9-4972-8237-91447486b125',
      test: '62016951-5835-4861-bb14-55cd9b6e7598',
      safeWorkingLoad: 'd3d84666-c1e0-418c-b951-933daf9b8cd5',
    },
  ],
  defectItems: [
    {
      itemNumber: null,
      subject: 'e8fcf3fb-d6e8-430b-8b08-3be195198a7d',
      comments: '91f49544-5c5a-417c-bf35-24340fa70aef',
    },
  ],
  safetyNotes: [
    {
      title: '65b13305-c42d-4e17-878d-6a28997911df',
    },
  ],
  signature: '8f86fa4f-d5bf-4384-81ba-33a0ae225a81',
}
